// import node module libraries
import { Fragment, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Row, Card, Form, Button, Spinner, Image } from 'react-bootstrap';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import axios
import axios from 'axios';

// import media files
import Logo from 'assets/images/brand/logo/logo-icon.svg';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const OTP = () => {
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [isInvalidUri, setIsInvalidUri] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner
	const [isButtonGenerateNewOTPDisabled, setIsButtonGenerateNewOTPDisabled] = useState(false);
	const [isButtonGenerateNewOTPLoading, setIsButtonGenerateNewOTPLoading] = useState(false); // State for loading spinner
	const [showButtonGenerateNewOTP, setShowButtonGenerateNewOTP] = useState(false);

	// Get URL Parameters
	const [searchParams] = useSearchParams();
	const action = searchParams.get("action");
	let redirect_uri = searchParams.get("redirect_uri");
	const username = searchParams.get("username");
	const otp = searchParams.get("otp");
	const valid_for = searchParams.get("valid_for");

	const navigate = useNavigate();

	useEffect(() => {
		// Check if the URL parameters are valid
		if (!action || !username || !valid_for) {
			handleShowNotification(setNotification, 'danger', 'Fehler', 'Ungültiger Link. Bitte überprüfe deinen Link oder kontaktiere den HelpDesk.');
			setIsInvalidUri(true);
			return;
		}
		if (!redirect_uri) {
			redirect_uri = '/dashboard/overview';
		}
		if (action === 'generate') {
			handleShowNotification(setNotification, 'info', 'Information', 'Einmalcode wurde erfolgreich generiert. Bitte überprüfe deine E-Mails.');
			return;
		}
		if (action === 'verify') {
			if (!valid_for || !otp || !username) {
				handleShowNotification(setNotification, 'danger', 'Fehler', 'Ungültiger Link. Bitte überprüfe deinen Link oder kontaktiere den HelpDesk.');
			} else {
				setIsButtonDisabled(true);
				setIsLoading(true); // Set loading to true
				const options = {
					headers: { 'Content-Type': 'application/json' },
				};
				const data = {
					username: username,
					otp_token: otp,
					otp_valid_for: valid_for
				};

				// Dynamische URL basierend auf der Umgebung
				const baseURL = process.env.REACT_APP_ENV === 'development'
					? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
					: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

				axios.post(`${baseURL}/auth/verify-otp`, data, options)
					.then(async response => {
						// Disable Form and Button after submission
						setIsVerified(true);
						if (valid_for === 'activate_account') {
							handleShowNotification(setNotification, 'info', 'Information', response.data.message + '. Du wirst nun in wenigen Sekunden weitergeleitet.');
							setTimeout(() => {
								navigate(redirect_uri);
							}, 4000);
						}
					})
					.catch((error) => {
						const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
						handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Solltest du nicht weiterkommen kontaktiere den HelpDesk.');
						setIsButtonDisabled(false);
						setIsLoading(false); // Stop loading
					});
			}
			return;
		}
	}, [otp, username]);

	const handleSubmitOTP = async (e) => {
		e.preventDefault();
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
		};

		const data = {
			username: username,
			otp_token: e.target.otp.value,
			otp_valid_for: valid_for
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/auth/verify-otp`, data, options)
			.then(response => {
				// Disable Form and Button after submission
				setIsSubmitted(true);
				handleShowNotification(setNotification, 'info', 'Information', response.data.message);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Solltest du nicht weiterkommen kontaktiere den HelpDesk.');
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
			});
	};

	const auth = useAuthUser(); // Assuming this gets the auth user data

	useEffect(() => {
		if (auth) {
			setShowButtonGenerateNewOTP(true);
		} else {
			setShowButtonGenerateNewOTP(false);
		}
	}, [auth]);

	const authHeader = useAuthHeader();

	const handleGenerateNewOTP = async (e) => {
		e.preventDefault();
		setIsButtonDisabled(true);
		setIsButtonGenerateNewOTPDisabled(true);
		setIsButtonGenerateNewOTPLoading(true); // Set loading to true

		// API-Call Options
		const options = {
			params: {

			},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};

		const data = {
			username: username,
			otp_valid_for: valid_for
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/auth/generate-otp`, data, options)
			.then(response => {
				// Disable Form and Button after submission
				handleShowNotification(setNotification, 'info', 'Information', response.data.message);
				setIsButtonDisabled(false);
				setIsButtonGenerateNewOTPLoading(false)
				setIsButtonGenerateNewOTPDisabled(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Solltest du nicht weiterkommen kontaktiere den HelpDesk.');
				setIsButtonDisabled(false);
				setIsButtonGenerateNewOTPDisabled(false);
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row className="align-items-center justify-content-center g-0 min-vh-100">
				<Col lg={5} md={5} className="py-8 py-xl-0">
					<Card>
						{isInvalidUri === true ? (
							<Card.Body className="p-6">
								<div className="mb-4">
									<Link to="/">
										<Image src={Logo} className="mb-4" alt="" />
									</Link>
									<h1 className="mb-1 fw-bold">Ungültiger Link</h1>
									<span>Der Link ist ungültig. Bitte überprüfe deinen Link oder kontaktiere den HelpDesk.</span>
								</div>
							</Card.Body>
						) : isVerified === true ? (
							<Card.Body className="p-6">
								<div className="mb-4">
									<Link to="/">
										<Image src={Logo} className="mb-4" alt="" />
									</Link>
									<h1 className="mb-1 fw-bold">Einmalcode erfolgreich verifiziert</h1>
									<span>Die Überprüfung war erfolgreich. Du wirst nun in wenigen Sekunden weitergeleitet.</span>
								</div>
							</Card.Body>
						) : (
							<Card.Body className="p-6">
								<div className="mb-4">
									<Link to="/">
										<Image src={Logo} className="mb-4" alt="" />
									</Link>
									<h1 className="mb-1 fw-bold">Einmalcode verifizieren</h1>
									<span>Bitte gebe hier deinen Einmalcode ein, den du erhalten hast.</span>
								</div>
								<Form onSubmit={handleSubmitOTP}>
									<Row>
										<Col lg={12} md={12} className="mb-3">
											<Form.Label>Einmalcode</Form.Label>
											<Form.Control
												type="text"
												id="otp"
												placeholder="OTP angeben"
												defaultValue={otp || ''}
												autoComplete="otp"
												required
											/>
										</Col>
										<Col lg={12} md={12} className="mb-3 d-grid gap-2">
												<Button variant="primary" type="submit" disabled={isButtonDisabled}>
												{isLoading ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														Einmalcode verifizieren
													</>
												) : (
													'Einmalcode verifizieren'
												)}
											</Button>
											{showButtonGenerateNewOTP && (	
											<Button variant="outline-secondary" disabled={isButtonGenerateNewOTPDisabled} onClick={handleGenerateNewOTP}>
												{isButtonGenerateNewOTPLoading ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														Neuen Einmalcode zusenden
													</>
												) : (
													'Neuen Einmalcode zusenden'
												)}
											</Button>
											)}
										</Col>
									</Row>
								</Form>
								<hr className="my-4" />
								<div className="mt-4 text-center">
									<span>
										Falls du die Überprüfung abbrechen möchtest, gehe zurück zur {' '}
										<Link to="/authentication/sign-in">
											Anmeldung
										</Link>
									</span>
								</div>
							</Card.Body>
						)}
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default OTP;